import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/PaymentMethodsService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the payment-methods list.
//
// ⚠️ SCOPE OF THIS CHANGE. payment-methods is MONEY-PATH ADJACENT — the module
// also renders Stripe Elements and is consumed by payment-intents. Only the
// LIST/TABLE layer is touched here. No payment logic, no Element wiring, and no
// confirm/capture flow is changed; `add`/`update` in the service are left
// exactly as they were.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['paymentMethods', customerId, currentToken]` — a bare string plus a
// page token, which makes the cache page-shaped rather than list-shaped.
export const paymentMethodsKeys = {
    all: ['payment-methods'] as const,
    lists: () => [...paymentMethodsKeys.all, 'list'] as const,
    list: (customerId: string, filters: Record<string, string> = {}) =>
        [...paymentMethodsKeys.lists(), customerId, {filters}] as const,
    details: () => [...paymentMethodsKeys.all, 'detail'] as const,
    detail: (id: string) => [...paymentMethodsKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack. The query
 * document already declared `$limit` and `$nextToken` and selected `nextToken`,
 * so the API side was fine — it was the client that could only hold one page at
 * a time.
 *
 * ⚠️ `type` IS SENT TO THE SERVER. `PaymentMethodsFilterInput` declares
 * `{customerId: String!, type: PaymentMethodType}`, so unlike the other lists
 * in this app this one can genuinely narrow server-side. It is in the queryKey,
 * so changing it refetches — omit a filter from the key and changing it
 * silently serves the previous filter's cache (§9).
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever.
 */
export const usePaymentMethodsInfiniteQuery = (
    customerId: string,
    filters: Record<string, string> = {},
) => {
    // Only `type` is a server filter; anything else stays client-side.
    const serverFilter: Record<string, string> = {customerId};
    if (filters.type) {
        serverFilter.type = filters.type;
    }

    return useInfiniteQuery({
        queryKey: paymentMethodsKeys.list(customerId, filters),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(serverFilter, PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
        // `customerId` is REQUIRED by the filter input, so firing without one
        // would be a guaranteed error rather than an empty list.
        enabled: !!customerId,
    });
};

/**
 * Batch delete (detach). The whole module key is invalidated on success so the
 * table reflects the removal without a manual reload (§8).
 *
 * ⚠️ The underlying `deleteRecord` service call is UNCHANGED — this wraps the
 * existing behaviour in structured keys and a notification, nothing more.
 */
export const useDeletePaymentMethodsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Payment method deleted'
                    : `${ids.length} payment methods deleted`,
            );
            queryClient.invalidateQueries({queryKey: paymentMethodsKeys.all});
        },
        onError: notifyError,
    });
};
